import {
  IBudgetPaymentMethod,
  TBudgetSigningMode,
} from 'modules/checkout/domain/entities';
import {
  PAYMENT_METHOD_TO_DOMAIN,
  TVectisPaymentMethodType,
} from 'shared/infra/adapters';

export interface IVectisPaymentMethod {
  key: TVectisPaymentMethodType;
  titulo: string;
  descricao: string;
  tipoAssinaturaContrato?: TBudgetSigningMode;
  elegivelParaFechamentoOrcamento?: {
    elegivel: boolean;
    mensagem?: string;
  } | null;
  maximoParcelas: number;
  valorParcela: number;
  percentualDesconto: number;
  precoDe: number;
  precoPor: number;
  percentualDescontoFormaPagamento: number;
  percentualDescontoFormaPagamentoEfetivo?: number;
  valorBaseParaDesconto?: number;
  valorDescontoTotal?: number;
  valorDescontoFormaPagamento: number;
  valorRecebimentoFormaPagamento?: number;
  diferencaParaValorPadraoOrcamento?: number;
  avisos?: string[] | null;
  bloquearFechamentoOrcamento?: boolean | null;
  parcelas?: {
    quantidade: number;
    valor: number;
    permiteRealizarPagamentoNessaCondicao?: boolean;
  }[];
}

/**
 * Traduz uma forma de pagamento do Vectis para o domínio.
 *
 * Mora fora do adaptador de orçamento porque o carrinho recebe a mesma estrutura sem
 * orçamento nenhum — a simulação devolve só a lista de formas. Ter um mapeamento só evita
 * que as duas telas voltem a divergir, que é o que este card existe para acabar.
 *
 * `fallbackSigningMode` cobre o período em que o backend ainda não manda a assinatura por
 * forma: no checkout vale a do orçamento; no carrinho não há orçamento, e o passo de
 * escolha da forma é o primeiro momento em que dá para saber.
 */
export function buildPaymentMethod(
  method: IVectisPaymentMethod,
  fallbackSigningMode: TBudgetSigningMode,
): IBudgetPaymentMethod {
  return {
    key: PAYMENT_METHOD_TO_DOMAIN[method.key],
    title: method.titulo,
    description: method.descricao,
    signingMode: method.tipoAssinaturaContrato ?? fallbackSigningMode,
    eligibility: method.elegivelParaFechamentoOrcamento
      ? {
          eligible: method.elegivelParaFechamentoOrcamento.elegivel,
          message: method.elegivelParaFechamentoOrcamento.mensagem,
        }
      : undefined,
    // O Vectis manda o aviso pronto e traduzido por tenância — é dele que sai, por exemplo,
    // o aviso de valor mínimo de parcela. A tela não monta texto próprio.
    warnings: method.avisos ?? [],
    blocksCheckout: method.bloquearFechamentoOrcamento ?? false,
    discount: {
      percentage: {
        default: method.percentualDesconto,
        byPaymentMethod: method.percentualDescontoFormaPagamento,
        applied: method.percentualDescontoFormaPagamentoEfetivo,
      },
      value: method.valorDescontoFormaPagamento,
    },
    values: {
      from: method.precoDe,
      to: method.precoPor,
      totalDiscount: method.valorDescontoTotal,
      discountInRelationToBudgetValue:
        method.diferencaParaValorPadraoOrcamento || 0,
    },
    installments: (method.parcelas ?? []).map(installment => ({
      number: installment.quantidade,
      value: installment.valor,
      allowed: installment.permiteRealizarPagamentoNessaCondicao ?? true,
    })),
  };
}

export function buildPaymentMethods(
  methods: IVectisPaymentMethod[],
  fallbackSigningMode: TBudgetSigningMode,
): IBudgetPaymentMethod[] {
  return (methods ?? [])
    .filter(method => !!method.key && !!PAYMENT_METHOD_TO_DOMAIN[method.key])
    .map(method => buildPaymentMethod(method, fallbackSigningMode));
}
